import { Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationService } from '@app/_services/notification.service';
import { PublishPlanService, PublishPlanStatusResponse } from './publish-plan.service';
import { ActivePlanService } from '@app/_services/active-plan.service';

interface PublishPlanProgressState {
  isVisible: boolean;
  message: string;
  runtimeStatus: string;
  statusQueryGetUri: string | null;
}

const STORAGE_KEY = 'publishPlanProgressState';
const DEFAULT_IN_PROGRESS_MESSAGE = 'Publish plan is in progress.';

@Injectable({
  providedIn: 'root'
})
export class PublishPlanProgressService {
  readonly state = signal<PublishPlanProgressState>({
    isVisible: false,
    message: '',
    runtimeStatus: '',
    statusQueryGetUri: null
  });

  private pollingSubscription?: Subscription;

  constructor(
    private publishPlanService: PublishPlanService,
    private notificationService: NotificationService,
    private activePlanService: ActivePlanService
  ) {
    this.restoreTracking();
  }

  startTracking(statusQueryGetUri: string, message: string = DEFAULT_IN_PROGRESS_MESSAGE): void {
    if (!statusQueryGetUri) {
      return;
    }

    this.stopPolling();
    this.setInProgressState(statusQueryGetUri, 'Running', message);

    this.pollingSubscription = this.publishPlanService
      .pollUntilComplete(statusQueryGetUri, 15000)
      .subscribe({
        next: (status) => this.handleStatus(status),
        error: (error) => this.handlePollingError(error)
      });
  }

  private restoreTracking(): void {
    const storedState = localStorage.getItem(STORAGE_KEY);

    if (!storedState) {
      return;
    }

    try {
      const parsedState = JSON.parse(storedState) as PublishPlanProgressState;

      if (!parsedState.statusQueryGetUri) {
        this.clearState();
        return;
      }

      this.startTracking(parsedState.statusQueryGetUri, parsedState.message || DEFAULT_IN_PROGRESS_MESSAGE);
    } catch {
      this.clearState();
    }
  }

  private handleStatus(status: PublishPlanStatusResponse): void {
    const runtimeStatus = status.runtimeStatus || 'Running';

    if (runtimeStatus === 'Completed') {
      this.clearState();
      this.showCompletionMessage(status.output);
      return;
    }

    if (runtimeStatus === 'Failed' || runtimeStatus === 'Terminated') {
      this.clearState();
      this.notificationService.showError(this.resolveOutputMessage(status.output) || `Orchestration ${runtimeStatus.toLowerCase()}.`);
      return;
    }

    this.setInProgressState(this.state().statusQueryGetUri, runtimeStatus, this.state().message || DEFAULT_IN_PROGRESS_MESSAGE);
  }

  private handlePollingError(error: unknown): void {
    this.clearState();
    const errorMessage = error instanceof Error ? error.message : 'Unable to get publish status.';
    this.notificationService.showError(`Polling failed: ${errorMessage}`);
  }

  private setInProgressState(
    statusQueryGetUri: string | null,
    runtimeStatus: string = 'Running',
    message: string = DEFAULT_IN_PROGRESS_MESSAGE
  ): void {
    const nextState: PublishPlanProgressState = {
      isVisible: true,
      message,
      runtimeStatus,
      statusQueryGetUri
    };

    this.state.set(nextState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
  }

  private showCompletionMessage(output: unknown): void {
    const published = this.resolvePublishedFlag(output);
    const completionMessage = this.resolveOutputMessage(output) || 'Plan published successfully.';

    if (published === false) {
      this.notificationService.showError(completionMessage);
      return;
    }

    this.activePlanService.markCurrentPlanPublished();
    this.notificationService.showSuccess(completionMessage);
  }

  private resolvePublishedFlag(output: unknown): boolean | undefined {
    if (!output || typeof output !== 'object') {
      return undefined;
    }

    const typedOutput = output as { published?: boolean; value?: { published?: boolean } };
    return typedOutput.published ?? typedOutput.value?.published;
  }

  private resolveOutputMessage(output: unknown): string {
    if (typeof output === 'string') {
      return output.trim();
    }

    if (!output || typeof output !== 'object') {
      return '';
    }

    const typedOutput = output as { message?: string; value?: { message?: string } };
    return typedOutput.message?.trim() || typedOutput.value?.message?.trim() || '';
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }

  private clearState(): void {
    this.stopPolling();
    this.state.set({
      isVisible: false,
      message: '',
      runtimeStatus: '',
      statusQueryGetUri: null
    });
    localStorage.removeItem(STORAGE_KEY);
  }
}